import {
  setAttack,
  setDecay,
  setSustain,
  setRelease,
  setAdsrEnabled,
  setFilterCutoff,
  setFilterType,
  setReverbMix,
  setReverbDecay,
  setDelayTime,
  setDelayFeedback,
  setDelayMix,
  setReverseMix,
  debugGlobalChain,
  isGlobalChainConnected,
} from "./audioUtils";
import { Step } from "../components/sequencer/types";
import { Register } from "./registerVoicing";

// Master preset interface that controls all aspects of the instrument
export interface MasterPreset {
  id: string;
  name: string;
  description: string;
  category:
    | "ambient"
    | "rhythmic"
    | "melodic"
    | "experimental"
    | "classic"
    | "custom";

  // Sample configuration
  sample: {
    id: string; // Sample ID from AVAILABLE_SAMPLES
  };

  // Pattern configuration
  pattern: {
    preset: string; // Key from PATTERN_PRESETS
  };

  // Chord/progression configuration (for chord mode)
  chord: {
    key: string;
    progression: string; // Key from progressions object
    roman?: string[]; // Saved chord sequence from current editor state
    mode?: "minor" | "major"; // Mode used by the saved chord sequence
    customRoman?: string[]; // Optional custom progression order/content
    customMode?: "minor" | "major"; // Mode to use with customRoman
  };

  // Effects configuration
  effects: {
    // ADSR Envelope
    attack: number;
    decay: number;
    sustain: number;
    release: number;
    adsrEnabled?: boolean;

    // Filter
    filterCutoff: number;
    filterType?: BiquadFilterType; // Optional for backward compatibility, defaults to "lowpass"

    // Reverb
    reverbMix: number;
    reverbDecay: number;

    // Delay
    delayTime: string; // Tone.js notation (e.g., "4n", "8n")
    delayFeedback: number;
    delayMix: number;

    // Reverse
    reverseMix?: number;
  };

  // Sequencer configuration
  sequencer: {
    bpm: number;
    mode: "chord" | "drone-mono" | "drone-duo";
  };

  // Step configuration
  stepsCount: number; // Number of steps (16/32/48/64)
  stepPattern: Step[]; // Complete step array with active states and fxMixes

  // Register configuration
  register: Register; // Current register value

  // Ribbon positions for InteractiveChordProgression (percentage-based)
  ribbonPositions: Array<{ startPercent: number; endPercent: number }>; // Empty array means default equal distribution

  // FX mode toggles (per-step vs global)
  fxModes: {
    filter: boolean; // true = per-step mode, false = global mode
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };

  // Drone mode configuration (optional, only for drone modes)
  droneMode?: "mono" | "duo"; // Drone mode if in drone sequencer mode
  droneNotes?: string[]; // Drone notes if in drone mode (e.g., ["C", "G"])

  // Performance parameters
  strum: number; // Strum amount (0-1)
  groove: number; // Groove amount (-1 to 1)
}

const buildStepPattern = (
  stepsCount: number,
  mode: Step["mode"],
  activeSteps: number[],
  fxMixesByStep: Record<number, Step["fxMixes"]> = {}
): Step[] =>
  Array.from({ length: stepsCount }, (_, index) => ({
    active: activeSteps.includes(index),
    mode,
    ...(fxMixesByStep[index] ? { fxMixes: fxMixesByStep[index] } : {}),
  }));

// Predefined master presets
export const MASTER_PRESETS: { [key: string]: MasterPreset } = {
  default: {
    id: "default",
    name: "Default",
    description: "Default application settings",
    category: "classic",
    sample: {
      id: "2ravie", // Changed from aircode to hello_dance
    },
    pattern: {
      preset: "triplet", // Changed from empty to triplet
    },
    chord: {
      key: "C", // Default key
      progression: "melancholic", // Default progression
    },
    effects: {
      attack: 0.001,
      decay: 0.3,
      sustain: 0.7,
      release: 1.0,
      filterCutoff: 10000,
      filterType: "lowpass",
      reverbMix: 0.3,
      reverbDecay: 2.0,
      delayTime: "4n",
      delayFeedback: 0.3,
      delayMix: 0.2,
      reverseMix: 0,
    },
    sequencer: {
      bpm: 120,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: [],
    register: { bottomNote: "C3", topNote: "C4" },
    ribbonPositions: [],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
    strum: 0,
    groove: 0,
  },

  ambient_pad: {
    id: "ambient_pad",
    name: "Ambient Pad",
    description: "Lush ambient soundscape with long reverb",
    category: "ambient",
    sample: {
      id: "soft_pad",
    },
    pattern: {
      preset: "minimal",
    },
    chord: {
      key: "Cm",
      progression: "melancholic",
    },
    effects: {
      attack: 0.5, // Reduced from 0.5 for less attack
      decay: 0.8,
      sustain: 0.9,
      release: 2.0,
      filterCutoff: 8000,
      filterType: "lowpass",
      reverbMix: 0.6,
      reverbDecay: 4.0,
      delayTime: "4n",
      delayFeedback: 0.4,
      delayMix: 0.3,
      reverseMix: 0,
    },
    sequencer: {
      bpm: 80,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: [],
    register: { bottomNote: "C3", topNote: "C4" },
    ribbonPositions: [],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
    strum: 0,
    groove: 0,
  },

  waveracer: {
    id: "waveracer",
    name: "waveracer",
    description: "Fast triplet drive with wide delay taps",
    category: "rhythmic",
    sample: {
      id: "hello_dance",
    },
    pattern: {
      preset: "triplet",
    },
    chord: {
      key: "C",
      progression: "melancholic",
    },
    effects: {
      attack: 0.001,
      decay: 0.3,
      sustain: 0,
      release: 1,
      filterCutoff: 10000,
      filterType: "lowpass",
      reverbMix: 0.3,
      reverbDecay: 2,
      delayTime: "8n",
      delayFeedback: 0.3,
      delayMix: 0.64,
      reverseMix: 0,
    },
    sequencer: {
      bpm: 166,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: [
      {
        active: true,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: true,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: true,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: true,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: true,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: false,
        mode: "chord",
      },
      {
        active: true,
        mode: "chord",
      },
    ],
    register: { bottomNote: "C3", topNote: "C4" },
    ribbonPositions: [
      { startPercent: 0, endPercent: 0.25 },
      { startPercent: 0.25, endPercent: 0.5 },
      { startPercent: 0.5, endPercent: 0.75 },
      { startPercent: 0.75, endPercent: 1 },
    ],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
    strum: 0,
    groove: 0,
  },

  glacial_bloom: {
    id: "glacial_bloom",
    name: "Glacial Bloom",
    description: "Slow, glassy pad with deep tails",
    category: "ambient",
    sample: {
      id: "soft_pad",
    },
    pattern: {
      preset: "minimal",
    },
    chord: {
      key: "Dm",
      progression: "emotional",
    },
    effects: {
      attack: 0.7,
      decay: 0.9,
      sustain: 0.85,
      release: 3.5,
      filterCutoff: 6500,
      filterType: "lowpass",
      reverbMix: 0.75,
      reverbDecay: 5.0,
      delayTime: "4n",
      delayFeedback: 0.35,
      delayMix: 0.25,
      reverseMix: 0,
    },
    sequencer: {
      bpm: 68,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: [],
    register: { bottomNote: "C2", topNote: "C4" },
    ribbonPositions: [],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
    strum: 0,
    groove: 0,
  },

  ghost_lagoon: {
    id: "ghost_lagoon",
    name: "Ghost Lagoon",
    description: "Shimmering minor haze with slow movement",
    category: "ambient",
    sample: {
      id: "ghost",
    },
    pattern: {
      preset: "triplet",
    },
    chord: {
      key: "Am",
      progression: "sad",
    },
    effects: {
      attack: 0.35,
      decay: 0.6,
      sustain: 0.85,
      release: 2.6,
      filterCutoff: 5000,
      filterType: "lowpass",
      reverbMix: 0.65,
      reverbDecay: 4.5,
      delayTime: "8n",
      delayFeedback: 0.45,
      delayMix: 0.3,
      reverseMix: 0.1,
    },
    sequencer: {
      bpm: 72,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: [],
    register: { bottomNote: "A2", topNote: "E4" },
    ribbonPositions: [
      { startPercent: 0, endPercent: 0.5 },
      { startPercent: 0.5, endPercent: 0.7 },
      { startPercent: 0.7, endPercent: 0.85 },
      { startPercent: 0.85, endPercent: 1 },
    ],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
    strum: 0,
    groove: 0,
  },

  plucking_lattice: {
    id: "plucking_lattice",
    name: "Plucking Lattice",
    description: "Tight, interlocking plucks",
    category: "melodic",
    sample: {
      id: "plucking",
    },
    pattern: {
      preset: "euclidean7",
    },
    chord: {
      key: "E",
      progression: "cheerful",
    },
    effects: {
      attack: 0.003,
      decay: 0.15,
      sustain: 0.5,
      release: 0.5,
      filterCutoff: 8000,
      filterType: "lowpass",
      reverbMix: 0.2,
      reverbDecay: 2.0,
      delayTime: "16n",
      delayFeedback: 0.25,
      delayMix: 0.3,
      reverseMix: 0,
    },
    sequencer: {
      bpm: 118,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: [],
    register: { bottomNote: "C3", topNote: "E4" },
    ribbonPositions: [],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
    strum: 0.1,
    groove: 0,
  },

  metro_drive: {
    id: "metro_drive",
    name: "Metro Drive",
    description: "Four-on-the-floor with tight transients",
    category: "rhythmic",
    sample: {
      id: "hello_dance",
    },
    pattern: {
      preset: "fourOnFloor",
    },
    chord: {
      key: "C",
      progression: "hopeful",
    },
    effects: {
      attack: 0.001,
      decay: 0.2,
      sustain: 0.2,
      release: 0.4,
      filterCutoff: 6000,
      filterType: "highpass",
      reverbMix: 0.15,
      reverbDecay: 1.6,
      delayTime: "16n",
      delayFeedback: 0.25,
      delayMix: 0.2,
      reverseMix: 0,
    },
    sequencer: {
      bpm: 126,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: [],
    register: { bottomNote: "C2", topNote: "C4" },
    ribbonPositions: [],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
    strum: 0,
    groove: 0,
  },

  euclid_pulse: {
    id: "euclid_pulse",
    name: "Euclid Pulse",
    description: "Metallic hits with per-step ambience",
    category: "rhythmic",
    sample: {
      id: "metallo",
    },
    pattern: {
      preset: "empty",
    },
    chord: {
      key: "F",
      progression: "nostalgic",
    },
    effects: {
      attack: 0.002,
      decay: 0.2,
      sustain: 0.3,
      release: 0.4,
      filterCutoff: 8000,
      filterType: "lowpass",
      reverbMix: 0.2,
      reverbDecay: 2.0,
      delayTime: "16n",
      delayFeedback: 0.2,
      delayMix: 0.2,
      reverseMix: 0,
    },
    sequencer: {
      bpm: 128,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: buildStepPattern(16, "chord", [0, 2, 4, 7, 9, 11, 14], {
      0: { reverb: 0.15 },
      4: { reverb: 0.35 },
      7: { reverb: 0.25 },
      11: { reverb: 0.4 },
      14: { reverb: 0.3 },
    }),
    register: { bottomNote: "C2", topNote: "C4" },
    ribbonPositions: [],
    fxModes: {
      filter: false,
      reverb: true,
      delay: false,
      reverse: false,
    },
    strum: 0,
    groove: 0,
  },

  reverse_tides: {
    id: "reverse_tides",
    name: "Reverse Tides",
    description: "Reverse swells on triplet hits",
    category: "experimental",
    sample: {
      id: "ghost",
    },
    pattern: {
      preset: "empty",
    },
    chord: {
      key: "C",
      progression: "melancholic",
    },
    effects: {
      attack: 0.02,
      decay: 0.4,
      sustain: 0.6,
      release: 1.2,
      filterCutoff: 6500,
      filterType: "lowpass",
      reverbMix: 0.45,
      reverbDecay: 3.2,
      delayTime: "8n",
      delayFeedback: 0.3,
      delayMix: 0.25,
      reverseMix: 0.5,
    },
    sequencer: {
      bpm: 92,
      mode: "chord",
    },
    stepsCount: 16,
    stepPattern: buildStepPattern(16, "chord", [0, 3, 6, 9, 12, 15], {
      0: { reverse: 0.2 },
      3: { reverse: 0.4 },
      6: { reverse: 0.6 },
      9: { reverse: 0.4 },
      12: { reverse: 0.8 },
      15: { reverse: 0.5 },
    }),
    register: { bottomNote: "C2", topNote: "C4" },
    ribbonPositions: [],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: true,
    },
    strum: 0,
    groove: 0,
  },

  drone_halo: {
    id: "drone_halo",
    name: "Drone Halo",
    description: "Slow duo drone with soft shimmer",
    category: "experimental",
    sample: {
      id: "soft_pad",
    },
    pattern: {
      preset: "minimal",
    },
    chord: {
      key: "C",
      progression: "hopeful",
    },
    effects: {
      attack: 0.6,
      decay: 0.8,
      sustain: 0.9,
      release: 3.0,
      filterCutoff: 5500,
      filterType: "lowpass",
      reverbMix: 0.7,
      reverbDecay: 5.5,
      delayTime: "2n",
      delayFeedback: 0.3,
      delayMix: 0.2,
      reverseMix: 0.2,
    },
    sequencer: {
      bpm: 55,
      mode: "drone-duo",
    },
    stepsCount: 16,
    stepPattern: [],
    register: { bottomNote: "C2", topNote: "C3" },
    ribbonPositions: [],
    fxModes: {
      filter: false,
      reverb: false,
      delay: false,
      reverse: false,
    },
    droneMode: "duo",
    droneNotes: ["C", "G"],
    strum: 0,
    groove: 0,
  },

};

// Helper function to get presets by category
export const getPresetsByCategory = (
  category: MasterPreset["category"]
): MasterPreset[] => {
  return Object.values(MASTER_PRESETS).filter(
    (preset) => preset.category === category
  );
};

// Helper function to get all preset categories
export const getAllCategories = (): MasterPreset["category"][] => {
  return ["classic", "ambient", "melodic", "rhythmic", "experimental"];
};

// Helper function to get a preset by ID
export const getPresetById = (id: string): MasterPreset | undefined => {
  return MASTER_PRESETS[id];
};

// Interface for master preset application callbacks
export interface MasterPresetCallbacks {
  onSampleChange: (sampleId: string) => void;
  onBpmChange: (bpm: number) => void;
  onKeyChange: (key: string) => void;
  onProgressionChange: (progression: string) => void;
  onCustomProgressionChange?: (progression: {
    name: string;
    roman: string[];
    mode: "minor" | "major";
  }) => void;
  onPatternPresetChange: (presetKey: string) => void;
  onStepsCountChange: (stepsCount: number) => void;
  onStepPatternChange: (stepPattern: Step[]) => void;
  onRegisterChange: (register: Register) => void;
  onRibbonPositionsChange: (
    ribbonPositions: Array<{ startPercent: number; endPercent: number }>
  ) => void;
  onFxModesChange: (fxModes: {
    filter: boolean;
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  }) => void;
  onSequencerModeChange: (mode: "chord" | "drone-mono" | "drone-duo") => void;
  onDroneNotesChange?: (droneNotes: string[]) => void;
  onStrumChange: (strum: number) => void;
  onGrooveChange: (groove: number) => void;
}

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * Applies a master preset to the instrument
 * @param preset The master preset to apply
 * @param callbacks Object containing callback functions for updating the UI
 */
export const applyMasterPreset = (
  preset: MasterPreset,
  callbacks: MasterPresetCallbacks
): Promise<void> => {
  return (async () => {
    try {
      console.log(`Applying master preset: ${preset.name}`);

      // Apply sample changes first
      callbacks.onSampleChange(preset.sample.id);

      callbacks.onBpmChange(preset.sequencer.bpm);

      // Apply sequencer mode
      callbacks.onSequencerModeChange(preset.sequencer.mode);

      // Apply chord settings (for chord mode)
      callbacks.onKeyChange(preset.chord.key);
      callbacks.onProgressionChange(preset.chord.progression);
      const savedRoman = preset.chord.roman ?? preset.chord.customRoman;
      const savedMode = preset.chord.mode ?? preset.chord.customMode;
      if (
        savedRoman &&
        savedRoman.length > 0 &&
        savedMode &&
        callbacks.onCustomProgressionChange
      ) {
        callbacks.onCustomProgressionChange({
          name: preset.chord.progression,
          roman: [...savedRoman],
          mode: savedMode,
        });
      }

      // Apply steps count
      callbacks.onStepsCountChange(preset.stepsCount);

      // Apply register
      callbacks.onRegisterChange(preset.register);

      // Apply ribbon positions
      callbacks.onRibbonPositionsChange(preset.ribbonPositions);

      // Apply FX modes
      callbacks.onFxModesChange(preset.fxModes);

      // Apply drone notes if available
      if (preset.droneNotes && callbacks.onDroneNotesChange) {
        callbacks.onDroneNotesChange(preset.droneNotes);
      }

      // Apply strum
      callbacks.onStrumChange(preset.strum);
      callbacks.onGrooveChange(preset.groove);

      // Apply pattern preset (this should happen before step pattern)
      callbacks.onPatternPresetChange(preset.pattern.preset);

      // Apply step pattern if not empty (delay slightly to ensure pattern preset is applied first)
      await wait(100);
      if (preset.stepPattern.length > 0) {
        callbacks.onStepPatternChange(preset.stepPattern);
      }

      // Apply effects settings with a delay and better error handling
      await wait(500); // ensure step voices are rebuilt before applying effects
      try {
        console.log(`Applying effects for preset: ${preset.name}`);

        // Check audio chain before applying effects
        console.log(
          `Audio chain connected before effects: ${isGlobalChainConnected()}`
        );

        // Apply effects one by one with error handling
        setAttack(preset.effects.attack);
        setDecay(preset.effects.decay);
        setSustain(preset.effects.sustain);
        setRelease(preset.effects.release);
        setAdsrEnabled(preset.effects.adsrEnabled ?? true);
        setFilterCutoff(preset.effects.filterCutoff);
        setFilterType(preset.effects.filterType || "lowpass");
        setReverbMix(preset.effects.reverbMix);
        setDelayTime(preset.effects.delayTime);
        setDelayFeedback(preset.effects.delayFeedback);
        setDelayMix(preset.effects.delayMix);
        if (typeof preset.effects.reverseMix === "number") {
          setReverseMix(preset.effects.reverseMix);
        }

        // Apply reverb decay last since it recreates the reverb node
        setReverbDecay(preset.effects.reverbDecay);

        // Check audio chain after applying effects
        console.log(
          `Audio chain connected after effects: ${isGlobalChainConnected()}`
        );
        if (!isGlobalChainConnected()) {
          console.warn("Audio chain disconnected after effects! Debugging...");
          debugGlobalChain();
        }

        console.log(`Effects applied successfully for preset: ${preset.name}`);
      } catch (effectError) {
        console.error(
          `Error applying effects for preset "${preset.name}":`,
          effectError
        );
        // Continue execution even if effects fail
      }

      console.log(`Master preset "${preset.name}" applied successfully`);
    } catch (error) {
      console.error(`Error applying master preset "${preset.name}":`, error);
    }
  })();
};
